import { useEffect, useRef, useState } from 'react';

export interface ContentFeature {
  icon: string;
  title: string;
  desc: string;
}

export interface SectionContentOverviewProps {
  eyebrow: string;
  title: string;
  titleAccent?: string;
  paragraphs: string[];
  features: ContentFeature[];
  image: string;
  imageAlt?: string;
}

export default function SectionContentOverview({
  eyebrow, title, titleAccent, paragraphs, features, image, imageAlt
}: SectionContentOverviewProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const scrollToEstimate = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={ref} className="py-24 md:py-32 bg-cream overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left content */}
          <div className={`flex-1 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-gold"></span>
              <span className="text-[11px] font-bold tracking-super uppercase text-gold">{eyebrow}</span>
            </div>
            <h2 className="font-condensed font-bold text-5xl md:text-6xl text-charcoal uppercase leading-none mb-3">
              {title}
              {titleAccent && (
                <>
                  <br />
                  <span className="font-serif font-normal italic text-gray-400 text-4xl md:text-5xl normal-case leading-tight">
                    {titleAccent}
                  </span>
                </>
              )}
            </h2>
            <div className="w-10 h-px bg-gold/40 my-6"></div>
            {paragraphs.map((p, i) => (
              <p key={i} className="text-gray-500 text-base leading-relaxed font-light mb-4 max-w-xl"
                dangerouslySetInnerHTML={{ __html: p }}></p>
            ))}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8 mb-10">
              {features.map((f, i) => (
                <div key={i} className="flex items-start gap-4 group">
                  <div className="w-9 h-9 flex items-center justify-center shrink-0 border border-gold/30 rounded group-hover:bg-gold/10 transition-colors">
                    <i className={`${f.icon} text-gold text-sm`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-charcoal text-sm mb-1">{f.title}</p>
                    <p className="text-gray-500 text-xs leading-relaxed font-light">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <a href="#estimate" onClick={scrollToEstimate}
              className="inline-flex items-center gap-3 bg-charcoal text-white font-bold text-[12px] tracking-widest uppercase px-8 py-4 rounded hover:bg-ink transition-colors cursor-pointer whitespace-nowrap">
              Schedule Your Free Inspection
              <i className="ri-arrow-right-line text-gold"></i>
            </a>
          </div>
          {/* Right image */}
          <div className={`w-full lg:w-[460px] shrink-0 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden">
              <img src={image} alt={imageAlt || title} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 ring-1 ring-gold/15 rounded-lg"></div>
              {/* Gold accent bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-gold via-gold/60 to-transparent"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
